class CollectionLoadMore extends HTMLElement {
  connectedCallback() {
    this.paginationType = this.getAttribute('data-pagination-type');
    this.nextUrl = this.getAttribute('data-next-url');
    this.isLoading = false;

    // Store the clean base URL (without ?page= parameter) to restore after each load.
    // This ensures the browser address bar never shows the page parameter.
    if (!CollectionLoadMore.cleanUrl) {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      CollectionLoadMore.cleanUrl = url.pathname + (url.search || '');
    }

    if (!this.nextUrl) return;

    if (this.paginationType === 'load_more') {
      this.button = this.querySelector('button');
      if (this.button) {
        this.button.addEventListener('click', this.loadNextPage.bind(this));
      }
    } else if (this.paginationType === 'infinite_scroll') {
      this.initInfiniteScroll();
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  initInfiniteScroll() {
    const options = {
      root: null, // Use the viewport
      rootMargin: '0px 0px 400px 0px', // Trigger load 400px before the element enters the viewport
      threshold: 0.0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isLoading) {
          this.loadNextPage();
        }
      });
    }, options);

    const trigger = this.querySelector('#InfiniteScrollTrigger');
    if (trigger) {
      this.observer.observe(trigger);
    }
  }

  /**
   * Build a section rendering API URL from the next page URL.
   * The page parameter is only used internally for the fetch request
   * and is never reflected in the browser's address bar.
   */
  buildSectionFetchUrl(nextPageUrl) {
    const url = new URL(nextPageUrl, window.location.origin);
    const sectionId = document.getElementById('product-grid')?.dataset.id;

    if (sectionId) {
      url.searchParams.set('section_id', sectionId);
    }

    return url.toString();
  }

  /**
   * Restore the browser URL to the clean collection URL (without ?page=).
   * Uses replaceState so it does not create a new history entry.
   */
  restoreCleanUrl() {
    const currentUrl = window.location.pathname + window.location.search;
    if (currentUrl !== CollectionLoadMore.cleanUrl) {
      history.replaceState(history.state, '', CollectionLoadMore.cleanUrl);
    }
  }

  async loadNextPage() {
    if (this.isLoading || !this.nextUrl) return;
    this.isLoading = true;

    // Show loading state
    if (this.paginationType === 'load_more' && this.button) {
      this.button.classList.add('loading');
      const spinner = this.button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      const trigger = this.querySelector('#InfiniteScrollTrigger');
      if (trigger) {
        const spinner = trigger.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
      }
    }

    try {
      // Use the section rendering API URL so only the section HTML is returned.
      // The page parameter is used only in this fetch request.
      const fetchUrl = this.buildSectionFetchUrl(this.nextUrl);
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error('Network response was not ok');
      const htmlText = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');

      // Get the product-grid elements
      const newGrid = doc.getElementById('product-grid');
      const currentGrid = document.getElementById('product-grid');

      if (newGrid && currentGrid) {
        const newProducts = Array.from(newGrid.querySelectorAll('li.grid__item'));
        
        // We use a temporary container to initialize scroll reveal animations
        // before attaching them to the document. This prevents existing products 
        // from re-animating.
        const tempContainer = document.createElement('div');
        newProducts.forEach((product) => tempContainer.appendChild(product));

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(tempContainer);
        }

        // Append the new elements to the current product grid
        while (tempContainer.firstChild) {
          currentGrid.appendChild(tempContainer.firstChild);
        }
      }


      // Extract new pagination/load more component
      const newLoadMore = doc.querySelector('collection-load-more');
      const currentLoadMoreContainer = this.parentElement;

      if (newLoadMore && currentLoadMoreContainer) {
        this.replaceWith(newLoadMore);
      } else {
        // No more products, remove the load more component
        this.remove();
      }

      // Ensure the browser URL stays clean after loading products.
      // This catches any URL changes made by Shopify's native scripts.
      this.restoreCleanUrl();
    } catch (error) {
      console.error('Failed to load more products:', error);
    } finally {
      this.isLoading = false;
    }
  }
}

// Static property to store the original clean URL across instances
CollectionLoadMore.cleanUrl = null;

if (!customElements.get('collection-load-more')) {
  customElements.define('collection-load-more', CollectionLoadMore);
}
